import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw, ArrowUpRight, Printer, CalendarClock, ClipboardX } from "lucide-react";
import DashFilterBar from "@/components/leadsdash/DashFilterBar";
import OverviewTab from "@/components/leadsdash/OverviewTab";
import CallingTab from "@/components/leadsdash/CallingTab";
import DispositionsTab from "@/components/leadsdash/DispositionsTab";
import AgentsTab from "@/components/leadsdash/AgentsTab";
import FollowupsTab from "@/components/leadsdash/FollowupsTab";
import AssignmentTab from "@/components/leadsdash/AssignmentTab";
import ContactQualityTab from "@/components/leadsdash/ContactQualityTab";
import TrendsTab from "@/components/leadsdash/TrendsTab";
import CampaignTab from "@/components/leadsdash/CampaignTab";

const REFRESH_MS = 45000;

export default function LeadsDashboard() {
  const { active } = useClient();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [quality, setQuality] = useState(null);
  const [my, setMy] = useState(null);
  const [loading, setLoading] = useState(true);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const f = filtersRef.current;
      const params = {};
      ["date_from", "date_to", "date_mode", "assignee", "classification",
       "disposition", "sector", "employee_count"].forEach((k) => { if (f[k]) params[k] = f[k]; });
      const [a, b, c, d] = await Promise.all([
        api.get("/leads/analytics", { params }),
        api.get("/leads/my-dashboard", { params }).catch(() => ({ data: null })),
        api.get("/leads/analytics/agents", { params }).catch(() => ({ data: null })),
        api.get("/leads/analytics/contact-quality", { params }).catch(() => ({ data: null })),
      ]);
      setData(a.data);
      setMy(b.data);
      setAgentData(c.data);
      setQuality(d.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, filters]);

  // Near-real-time: silent auto-refresh so agent submissions show up.
  useEffect(() => {
    const t = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // Drill-down → Leads list, carrying both the clicked metric + active filters.
  const drill = useCallback((extra) => {
    const f = filtersRef.current;
    const params = new URLSearchParams();
    if (f.assignee) params.set("assignee", f.assignee);
    if (f.classification) params.set("classification", f.classification);
    if (f.disposition) params.set("disposition", f.disposition);
    Object.entries(extra).forEach(([k, v]) => params.set(k, v));
    navigate(`/leads?${params.toString()}`);
  }, [navigate]);

  if (loading && !data) {
    return <div className="text-slate-400 p-6 text-sm" data-testid="leads-dashboard-loading">Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="leads-dashboard">
      <div className="flex items-center justify-between print:hidden">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Lead Management</div>
          <h1 className="font-heading text-lg font-semibold text-slate-900">{active?.name} · Dashboards</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400" data-testid="dash-updated-at">
            Updated {data?.generated_at ? new Date(data.generated_at).toLocaleTimeString() : "—"} · auto-refreshes
          </span>
          <Button variant="outline" size="sm" onClick={() => load()} className="h-8" data-testid="dashboard-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="h-8" data-testid="dashboard-print">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> PDF
          </Button>
          <Button asChild size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700">
            <Link to="/leads" data-testid="go-to-leads">Open Leads <ArrowUpRight className="w-3 h-3 ml-1" /></Link>
          </Button>
        </div>
      </div>

      <div className="print:hidden">
        <DashFilterBar filters={filters} onChange={setFilters} />
      </div>

      {data && (
        <Tabs defaultValue={active?.kind !== "leads" ? "campaign" : "overview"}>
          <TabsList className="print:hidden flex-wrap h-auto">
            {active?.kind !== "leads" && (
              <TabsTrigger value="campaign" data-testid="dash-tab-btn-campaign">Campaign</TabsTrigger>
            )}
            <TabsTrigger value="overview" data-testid="dash-tab-btn-overview">Overview</TabsTrigger>
            <TabsTrigger value="calling" data-testid="dash-tab-btn-calling">Calling</TabsTrigger>
            <TabsTrigger value="dispositions" data-testid="dash-tab-btn-dispositions">Dispositions</TabsTrigger>
            <TabsTrigger value="agents" data-testid="dash-tab-btn-agents">Agents &amp; Teams</TabsTrigger>
            <TabsTrigger value="followups" data-testid="dash-tab-btn-followups">Follow-ups</TabsTrigger>
            <TabsTrigger value="assignment" data-testid="dash-tab-btn-assignment">Assignment</TabsTrigger>
            <TabsTrigger value="quality" data-testid="dash-tab-btn-quality">Contact Quality</TabsTrigger>
            <TabsTrigger value="trends" data-testid="dash-tab-btn-trends">Trends</TabsTrigger>
          </TabsList>
          {active?.kind !== "leads" && (
            <TabsContent value="campaign" className="mt-4">
              <CampaignTab filters={filters} navigate={navigate} />
            </TabsContent>
          )}
          <TabsContent value="overview" className="mt-4 space-y-4">
            <OverviewTab data={data} drill={drill} />
            <div className="grid md:grid-cols-2 gap-4">
              <MyCallbacks my={my} />
              <MyIncompleteCaptures my={my} />
            </div>
          </TabsContent>
          <TabsContent value="calling" className="mt-4">
            <CallingTab data={data} drill={drill} />
          </TabsContent>
          <TabsContent value="dispositions" className="mt-4">
            <DispositionsTab data={data} drill={drill} />
          </TabsContent>
          <TabsContent value="agents" className="mt-4">
            <AgentsTab data={agentData} drill={drill} />
          </TabsContent>
          <TabsContent value="followups" className="mt-4">
            <FollowupsTab data={agentData} />
          </TabsContent>
          <TabsContent value="assignment" className="mt-4">
            <AssignmentTab data={agentData} drill={drill} onReload={() => load(true)} />
          </TabsContent>
          <TabsContent value="quality" className="mt-4">
            <ContactQualityTab data={quality} />
          </TabsContent>
          <TabsContent value="trends" className="mt-4">
            <TrendsTab filters={filters} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function MyIncompleteCaptures({ my }) {
  const list = my?.incomplete_capture || [];
  const count = my?.incomplete_capture_count ?? list.length;
  return (
    <Card className={count > 0 ? "border-rose-200" : ""}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ClipboardX className="w-4 h-4 text-rose-600" /> My Incomplete Captures
          {count > 0 && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
              {count}
            </span>
          )}
        </CardTitle>
        <p className="text-[10px] text-slate-400 font-normal">
          Calls you handled with no disposition recorded (e.g. wrap-up timeout) — open each lead and close it out.
        </p>
      </CardHeader>
      <CardContent className="p-2 text-xs" data-testid="my-incomplete-captures">
        {list.length === 0 ? (
          <div className="text-slate-400 p-2">Nothing pending — every call you handled has a disposition.</div>
        ) : (
          <div className="divide-y">
            {list.map((c) => (
              <Link key={c.id} to={`/leads/${c.id}`}
                    className="flex items-center justify-between px-2 py-2 hover:bg-rose-50/40"
                    data-testid={`incomplete-${c.customer_id}`}>
                <div>
                  <div className="font-medium text-slate-800">{c.customer_name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{c.customer_id}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-[11px] text-slate-700">
                    {c.attempts} dial{c.attempts === 1 ? "" : "s"}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {c.last_dialed_at ? `last ${String(c.last_dialed_at).slice(0, 16).replace("T", " ")}` : "—"}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MyCallbacks({ my }) {
  const list = my?.upcoming_callbacks || [];
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-amber-600" /> My Upcoming Callbacks
        </CardTitle>
      </CardHeader>
      <CardContent className="p-2 text-xs" data-testid="my-callbacks">
        {list.length === 0 ? (
          <div className="text-slate-400 p-2">No upcoming callbacks assigned to you.</div>
        ) : (
          <div className="divide-y">
            {list.map((c) => (
              <Link key={c.id} to={`/leads/${c.id}`}
                    className="flex items-center justify-between px-2 py-2 hover:bg-slate-50"
                    data-testid={`cb-${c.customer_id}`}>
                <div>
                  <div className="font-medium text-slate-800">{c.customer_name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{c.customer_id}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-[11px] text-slate-700">
                    {c.follow_up_at ? new Date(c.follow_up_at).toLocaleString() : "—"}
                  </div>
                  <div className="text-[10px] text-slate-500">{c.disposition}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
